import type { ErrorKind } from '../../types/ui.ts';

// Honest, distinct copy per failure mode. `not_configured` is a design state
// (no source id yet), not an error — it reads differently from an outage.
const COPY: Record<ErrorKind, { title: string; hint: string }> = {
  not_configured: {
    title: 'Not configured',
    hint: 'No source is set for this race yet. Nothing is being shown rather than a guess.',
  },
  unreachable: {
    title: 'Source unreachable',
    hint: 'Could not reach the provider. Other panels are unaffected.',
  },
  timeout: {
    title: 'Source timed out',
    hint: 'The provider did not respond in time. It will be retried on the next refresh.',
  },
  invalid: {
    title: 'Unreadable data',
    hint: 'The source returned data in an unexpected shape, so nothing is shown.',
  },
};

/** Independent per-panel empty/error state. A single failing source shows one of
 *  these; it never blanks the app. */
export function SourceState({
  label,
  kind,
  loading,
  message,
}: {
  label?: string;
  kind: ErrorKind | null;
  loading?: boolean;
  message?: string | null;
}) {
  if (loading) {
    return (
      <p className="source-state source-state--loading" role="status">
        Loading{label ? ` ${label}` : ''}…
      </p>
    );
  }
  const copy = kind ? COPY[kind] : { title: 'No data', hint: 'Nothing to show yet.' };
  return (
    <div className={`source-state source-state--${kind ?? 'empty'}`} role="status">
      <p className="source-state__title">
        {label ? <span className="source-state__label">{label}: </span> : null}
        {copy.title}
      </p>
      <p className="source-state__hint">{message ?? copy.hint}</p>
    </div>
  );
}
